import { createContext, useState } from 'react';
import type { ServiceOccupant } from './apiHelpers';

export const ServiceMultiSelectionContext = createContext<{
  multiSelectionOccupantCount: number;
  isMultiSelectingOccupants: boolean;
  multiSelectedOccupants: ServiceOccupant[];
  toggleMultiSelection: () => void;
  isOccupantMultiSelected: (occupant: ServiceOccupant) => boolean;
  updateOccupantMultiSelection: (occupant: ServiceOccupant) => void;
  updateOccupantMultiSelectionGroup: (occupants: ServiceOccupant[]) => void;
}>({
  multiSelectedOccupants: [],
  multiSelectionOccupantCount: 0,
  isMultiSelectingOccupants: false,
  toggleMultiSelection: () => {},
  isOccupantMultiSelected: () => false,
  updateOccupantMultiSelection: () => {},
  updateOccupantMultiSelectionGroup: () => {},
});

export const ServiceMultiSelectionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isMultiSelectingOccupants, setIsMultiSelectingOccupants] =
    useState(false);
  const [multiSelectedOccupants, setMultiSelectedOccupants] = useState<
    ServiceOccupant[]
  >([]);

  const toggleMultiSelection = () => {
    setIsMultiSelectingOccupants(!isMultiSelectingOccupants);
    setMultiSelectedOccupants([]);
  };

  const updateOccupantMultiSelection = (occupant: ServiceOccupant) => {
    const isOccupantAlreadySelected = isOccupantMultiSelected(occupant);
    if (isOccupantAlreadySelected) {
      unselectOccupant(occupant);
    } else {
      selectOccupant(occupant);
    }
  };

  const updateOccupantMultiSelectionGroup = (occupants: ServiceOccupant[]) => {
    const isEveryOccupantInGroupSelected = occupants.every((o) =>
      isOccupantMultiSelected(o),
    );
    if (isEveryOccupantInGroupSelected) {
      occupants.forEach((o) => {
        unselectOccupant(o);
      });
    } else {
      occupants.forEach((o) => {
        if (!isOccupantMultiSelected(o)) {
          selectOccupant(o);
        }
      });
    }
  };

  const selectOccupant = (occupant: ServiceOccupant) => {
    setMultiSelectedOccupants((previousSelection) => [
      ...previousSelection,
      occupant,
    ]);
  };

  const unselectOccupant = (occupant: ServiceOccupant) => {
    setMultiSelectedOccupants((previousSelection) =>
      previousSelection.filter((o) => o.id !== occupant.id),
    );
  };

  const isOccupantMultiSelected = (occupant: ServiceOccupant) => {
    return multiSelectedOccupants.some((o) => o.id === occupant.id);
  };

  return (
    <ServiceMultiSelectionContext.Provider
      value={{
        multiSelectedOccupants,
        multiSelectionOccupantCount: multiSelectedOccupants.length,
        isMultiSelectingOccupants,
        toggleMultiSelection,
        isOccupantMultiSelected,
        updateOccupantMultiSelection,
        updateOccupantMultiSelectionGroup,
      }}
    >
      {children}
    </ServiceMultiSelectionContext.Provider>
  );
};
